// @ts-nocheck
import { Component, Input, Output, EventEmitter, ChangeDetectionStrategy } from '@angular/core';
import { LoggerService } from '@way/theme-host/services/logger.service';
import { CommonModule } from '@angular/common';
import { FormsModule } from '@angular/forms';
import { CustomDatePickerComponent } from '@way/theme-host/components/custom-date-picker/custom-date-picker.component';

export interface BookingLayout2Data {
  title?: string;
  description?: string;
  buttonText?: string;
  buttonBackgroundColor?: string;
  buttonTextColor?: string;
  backgroundColor?: string;
  // formFields removed - form structure is static in the layout component
}

/**
 * Preview component for a two-column booking form layout that renders form fields, manages local form state, and emits interaction events for use in a website builder.
 * @component
 * @selector app-booking-layout-2-preview
 * @example
 *   <app-booking-layout-2-preview
 *     [data]="layoutData"
 *     [headingColor]="'#222'"
 *     [paragraphColor]="'#666'"
 *     [headingFont]="'Roboto'"
 *     [bodyFont]="'Open Sans'"
 *     [primaryColor]="'#ff6a00'"
 *     [formData]="initialFormValues"
 *     (formSubmit)="onSubmit($event)"
 *     (fieldChange)="onFieldChange($event)"
 *     (dateTimeChange)="onDateTimeChange($event)"
 *   ></app-booking-layout-2-preview>
 * @input {BookingLayout2Data | null} data - Layout content and labels (title, description, button text) supplied by the API for the preview.
 * @input {string | null} headingColor - Optional color override for heading text.
 * @input {string | null} paragraphColor - Optional color override for paragraph/body text.
 * @input {string | null} headingFont - Optional font family for headings.
 * @input {string | null} bodyFont - Optional font family for body text.
 * @input {string | null} primaryColor - Optional primary color (used for buttons/accents).
 * @input {Record<string, any>} formData - Initial form values to populate the preview form.
 * @output {{ formData: Record<string, any>; event: Event }} formSubmit - Emitted when the form is submitted; contains merged form data and the original submit event.
 * @output {{ field: any; value: any }} fieldChange - Emitted whenever a single form field value is updated (field metadata and new value).
 * @output {{ field: any; dateTime: Date | null }} dateTimeChange - Emitted when a date/time value is changed via the date picker (field metadata and resulting Date or null).
 *
 * Purpose:
 *   Renders a fixed-structure booking form (name, email, phone, date/time, textarea), handles local value storage, synchronizes with provided initial values, and exposes events so the parent editor can react to user input.
 *
 * Key methods:
 *   - getFormFields(): any[] - Returns the static array of form field definitions used by the layout (id, name, type, label, placeholder, required).
 *   - getFormRows(): any[][] - Groups fields into rows (two-per-row except textarea spans full width) for layout rendering.
 *   - getSelectOptions(field): Array<{value: string; label: string; disabled?: boolean}> - Returns select options for a given field (if present).
 *   - isDateField(field): boolean - Detects date/datetime-like fields.
 *   - isTimeField(field): boolean - Detects time fields.
 *   - shouldUseDatePicker(field): boolean - Decides if a custom date picker should be used for a field.
 *   - getTimeFieldForDate(dateField): any | null - Finds a related time field for a date field by name/id heuristics.
 *   - shouldHideTimeField(currentField): boolean - Determines whether a time field should be hidden because a related date field is present.
 *   - getFieldValue(field): any - Resolves current field value from local bookingFormData or provided formData.
 *   - setFieldValue(field, value): void - Updates local bookingFormData and emits fieldChange.
 *   - onFieldInput(field, event): void - Input handler for text/textarea inputs that sets value.
 *   - onFieldChange(field, event): void - Change handler for select elements that sets value.
 *   - getDateTimeValue(dateField): Date | null - Constructs a Date by combining date and related time fields if available.
 *   - onDateTimeChange(dateField, dateTime): void - Updates date and related time field values, emits dateTimeChange (runs asynchronously via setTimeout to allow UI updates).
 *   - onFormSubmit(event): void - Prevents default, stops propagation and emits formSubmit with merged form data.
 *   - onFormClick(event): void - Prevents outer propagation for clicks except when interacting with the custom date picker UI.
 *   - trackByRowIndex(index): number - TrackBy function for row iteration.
 *   - trackByFieldId(index, field): string - TrackBy function for field iteration.
 *
 * Important notes / lifecycle:
 *   - The component uses an internal bookingFormData object to accumulate runtime edits and merges this with the @input formData on submit.
 *   - No explicit Angular lifecycle hooks (ngOnInit/ngOnChanges) are required for typical usage; date/time updates use a setTimeout to schedule synchronous model updates and emissions.
 */
@Component({
  selector: 'app-booking-layout-2-preview',
  standalone: true,
  imports: [CommonModule, FormsModule, CustomDatePickerComponent],
  templateUrl: './booking-layout-2.component.html',
  changeDetection: ChangeDetectionStrategy.Eager,
  styleUrls: ['./booking-layout-2.component.scss'],
})
export class BookingLayout2PreviewComponent {
  constructor(private logger: LoggerService) {}

  @Input() data: BookingLayout2Data | null = null;
  @Input() headingColor: string | null = null;
  @Input() paragraphColor: string | null = null;
  @Input() headingFont: string | null = null;
  @Input() bodyFont: string | null = null;
  @Input() primaryColor: string | null = null;
  @Input() formData: Record<string, any> = {};
  @Output() formSubmit = new EventEmitter<{ formData: Record<string, any>; event: Event }>();
  @Output() fieldChange = new EventEmitter<{ field: any; value: any }>();
  @Output() dateTimeChange = new EventEmitter<{ field: any; dateTime: Date | null }>();

  // Form data storage
  private bookingFormData: Record<string, any> = {};

  /**
   * Get form fields - static default fields since form structure is fixed in layout
   */
  getFormFields(): any[] {
    // Return static form fields - the layout has a fixed structure
    // Only title, description, and button text are dynamic from API
    return [
      {
        id: 'full_name',
        name: 'full_name',
        label: 'Full Name',
        type: 'text',
        placeholder: 'Enter your full name',
        required: true,
      },
      {
        id: 'email',
        name: 'email',
        label: 'Email Address',
        type: 'email',
        placeholder: 'Enter your email',
        required: true,
      },
      {
        id: 'phone',
        name: 'phone',
        label: 'Phone Number',
        type: 'tel',
        placeholder: 'Enter your phone number',
        required: true,
      },
      {
        id: 'date',
        name: 'date',
        label: 'Date',
        type: 'date',
        placeholder: 'Select date and time',
        required: true,
      },
      {
        id: 'special_requests',
        name: 'special_requests',
        label: 'Special Requests or Notes',
        type: 'textarea',
        placeholder: 'Any special request or vehicle details',
        required: false,
      },
    ];
  }

  /**
   * Get form rows (2 fields per row, except textarea which spans full width)
   */
  getFormRows(): any[][] {
    const fields = this.getFormFields();
    if (!fields || fields.length === 0) return [];

    const rows: any[][] = [];
    let i = 0;

    while (i < fields.length) {
      const currentField = fields[i];

      // If it's a textarea, put it in its own row
      if (currentField?.type === 'textarea') {
        rows.push([currentField]);
        i++;
      } else {
        // For other fields, try to pair them (2 per row)
        const row = fields.slice(i, i + 2);
        rows.push(row);
        i += row.length;
      }
    }

    return rows;
  }

  /**
   * Get select options for a field
   */
  getSelectOptions(field: any): { value: string; label: string; disabled?: boolean }[] {
    if (!field) return [];
    return field.options || [];
  }

  /**
   * Check if field is a date field
   */
  isDateField(field: any): boolean {
    return field?.type === 'date' || field?.type === 'datetime' || field?.type === 'datetime-local';
  }

  /**
   * Check if field is a time field
   */
  isTimeField(field: any): boolean {
    return field?.type === 'time';
  }

  /**
   * Check if field should use date picker
   */
  shouldUseDatePicker(field: any): boolean {
    if (!field) return false;
    return this.isDateField(field) || field?.type === 'datetime-local';
  }

  /**
   * Get time field for a date field
   */
  getTimeFieldForDate(dateField: any): any | null {
    if (!dateField) return null;
    const fields = this.getFormFields();
    const dateFieldName = (dateField?.name || dateField?.id || '').toLowerCase();

    return (
      fields.find(f => {
        if (!this.isTimeField(f)) return false;
        const timeFieldName = (f.name || f.id || '').toLowerCase();
        const baseName = dateFieldName.replace('date', '').replace('_', '').trim();
        const timeBaseName = timeFieldName.replace('time', '').replace('_', '').trim();
        return (
          baseName === timeBaseName ||
          timeFieldName.includes(baseName) ||
          dateFieldName.includes(timeBaseName)
        );
      }) || null
    );
  }

  /**
   * Check if time field should be hidden
   */
  shouldHideTimeField(currentField: any): boolean {
    if (!this.isTimeField(currentField)) return false;
    const fields = this.getFormFields();
    return fields.some(f => {
      if (this.isDateField(f)) {
        const associatedTimeField = this.getTimeFieldForDate(f);
        return (
          associatedTimeField &&
          (associatedTimeField.id === currentField.id ||
            associatedTimeField.name === currentField.name)
        );
      }
      return false;
    });
  }

  /**
   * Get field value
   */
  getFieldValue(field: any): any {
    const fieldKey = field?.name || field?.id;
    return this.bookingFormData[fieldKey] || this.formData[fieldKey] || '';
  }

  /**
   * Set field value
   */
  setFieldValue(field: any, value: any): void {
    const fieldKey = field?.name || field?.id;
    this.bookingFormData[fieldKey] = value;
    this.fieldChange.emit({ field, value });
  }

  /**
   * Handle field input
   */
  onFieldInput(field: any, event: Event): void {
    const target = event.target as HTMLInputElement | HTMLTextAreaElement;
    if (target) {
      this.setFieldValue(field, target.value);
    }
  }

  /**
   * Handle field change (select)
   */
  onFieldChange(field: any, event: Event): void {
    const target = event.target as HTMLSelectElement;
    if (target) {
      this.setFieldValue(field, target.value);
    }
  }

  /**
   * Get date/time value
   */
  getDateTimeValue(dateField: any): Date | null {
    try {
      if (!dateField) return null;
      const timeField = this.getTimeFieldForDate(dateField);

      const dateKey = dateField?.name || dateField?.id;
      const dateValue = this.getFieldValue({ name: dateKey, id: dateKey });
      const timeValue = timeField ? this.getFieldValue(timeField) : null;

      if (!dateValue) return null;

      const date = new Date(dateValue);
      if (isNaN(date.getTime())) return null;

      if (timeValue) {
        const [hours, minutes] = timeValue.split(':');
        if (hours && minutes) {
          date.setHours(parseInt(hours, 10), parseInt(minutes, 10), 0, 0);
        }
      }
      return date;
    } catch {
      return null;
    }
  }

  /**
   * Handle date/time change
   */
  onDateTimeChange(dateField: any, dateTime: Date | null): void {
    setTimeout(() => {
      try {
        if (!dateField) return;
        const timeField = this.getTimeFieldForDate(dateField);
        const dateKey = dateField?.name || dateField?.id;

        if (!dateTime) {
          this.setFieldValue({ name: dateKey, id: dateKey }, '');
          if (timeField) {
            const timeKey = timeField?.name || timeField?.id;
            this.setFieldValue({ name: timeKey, id: timeKey }, '');
          }
          return;
        }

        const dateStr = dateTime.toISOString().split('T')[0];
        this.setFieldValue({ name: dateKey, id: dateKey }, dateStr);

        if (timeField) {
          const timeKey = timeField?.name || timeField?.id;
          const hours = dateTime.getHours().toString().padStart(2, '0');
          const minutes = dateTime.getMinutes().toString().padStart(2, '0');
          this.setFieldValue({ name: timeKey, id: timeKey }, `${hours}:${minutes}`);
        }

        this.dateTimeChange.emit({ field: dateField, dateTime });
      } catch (error) {
        this.logger.error('Error setting date/time value:', error);
      }
    }, 0);
  }

  /**
   * Handle form submission
   */
  onFormSubmit(event: Event): void {
    event.preventDefault();
    event.stopPropagation();
    this.formSubmit.emit({ formData: { ...this.formData, ...this.bookingFormData }, event });
  }

  /**
   * Handle form click
   */
  onFormClick(event: Event): void {
    const target = event.target as HTMLElement;
    if (!target) return;

    const isDatePickerClick =
      target.closest('.custom-date-picker') !== null ||
      target.closest('.date-picker-wrapper') !== null ||
      target.closest('.date-picker-input') !== null ||
      target.closest('.date-picker-dropdown') !== null ||
      target.classList.contains('custom-date-picker') ||
      target.classList.contains('date-picker-input') ||
      target.classList.contains('date-picker-dropdown');

    if (!isDatePickerClick) {
      event.stopPropagation();
    }
  }

  /**
   * Track by function for rows
   */
  trackByRowIndex(index: number): number {
    return index;
  }

  /**
   * Track by function for fields
   */
  trackByFieldId(index: number, field: any): string {
    return field?.id || field?.name || index.toString();
  }

  trackByIndex(index: number): number {
    return index;
  }
  trackById(_: number, item: any): any {
    return item?.id ?? item;
  }
  trackByValue(_: number, item: any): any {
    return item?.value ?? item;
  }
  trackByString(_: number, s: any): any {
    return s;
  }
}
